import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import {
  baoCaoGetChiTietHoTroThanhVienAction,
  baoCaoClearChiTietHoTroThanhVien,
} from '@app/store/reducers/baocao';

import {
  mapTrangThaiHoTroLabel,
  mapLoaiLoiLabel,
  mapMucDoPhucTapLabel,
} from '@app/store/constants';

import { objectShowNumber, rangeDateReport, messageToast } from '@app/utils/define';

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('vi-VN');
};

const cellWrapStyle = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  verticalAlign: 'middle',
};

const textCellStyle = {
  ...cellWrapStyle,
  textAlign: 'left',
};

const contentCellStyle = {
  ...cellWrapStyle,
  whiteSpace: 'pre-wrap',
  textAlign: 'left',
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };
  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };
  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const findPresetLabelByFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (!f && !t) return '';

  for (let i = 0; i < RANGE_OPTIONS.length; i++) {
    const label = RANGE_OPTIONS[i];
    if (label === RANGE_CUSTOM) continue;

    const { from: pf, to: pt } = getRangeFromTo(label);
    if (pf === f && pt === t) return label;
  }

  return RANGE_CUSTOM;
};

const getKhoaIds = (row) => row?.khoaIds ?? row?.KhoaIds ?? [];
const getKhoaCount = (row) => {
  const ids = getKhoaIds(row);
  return Array.isArray(ids) ? ids.length : 0;
};
const isHoTroDone = (row) => {
  const st = row?.statusValue ?? row?.StatusValue ?? row?.status ?? row?.Status;
  if (st === 1 || String(st) === '1') return true;
  if (typeof st === 'string' && st.toLowerCase() === 'hoanthanh') return true;
  return false;
};

const BaoCaoChiTietHoTroThanhVien = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    chiTietHoTroThanhVien,
    loadingChiTietHoTroThanhVien,
    errorChiTietHoTroThanhVien,
  } = useSelector((s) => s.baocao || {});

  const qs = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const nguoiDungId = qs.get('nguoiDungId') || '';

  const initialFrom = qs.get('From') || '';
  const initialTo = qs.get('To') || '';
  const initialRangeLabel = useMemo(
    () => findPresetLabelByFromTo(initialFrom, initialTo),
    [initialFrom, initialTo]
  );
  const returnUrl = qs.get('returnUrl') || '';

  const [rangeLabel, setRangeLabel] = useState(initialRangeLabel);
  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [appliedFrom, setAppliedFrom] = useState(initialFrom);
  const [appliedTo, setAppliedTo] = useState(initialTo);

  const syncUrl = (from, to) => {
    const sp = new URLSearchParams();
    sp.set('nguoiDungId', String(nguoiDungId));
    if (from) sp.set('From', String(from));
    if (to) sp.set('To', String(to));
    if (returnUrl) sp.set('returnUrl', String(returnUrl));

    history.replace({
      pathname: location.pathname,
      search: `?${sp.toString()}`,
    });
  };

  useEffect(() => {
    setRangeLabel(initialRangeLabel);
    setFromDate(initialFrom);
    setToDate(initialTo);
    setAppliedFrom(initialFrom);
    setAppliedTo(initialTo);
  }, [initialRangeLabel, initialFrom, initialTo]);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
      syncUrl(from, to);
      return;
    }

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      syncUrl('', '');
    }
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    const f = fromDate || '';
    const t = toDate || '';
    setAppliedFrom(f);
    setAppliedTo(t);
    syncUrl(f, t);
  };

  const handleClearFilter = () => {
    setRangeLabel(initialRangeLabel);
    setFromDate(initialFrom);
    setToDate(initialTo);
    setAppliedFrom(initialFrom);
    setAppliedTo(initialTo);
    syncUrl(initialFrom, initialTo);
  };

  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (!nguoiDungId) return;

    dispatch(
      baoCaoGetChiTietHoTroThanhVienAction({
        filter: {
          nguoiDungId,
          From: appliedFrom,
          To: appliedTo,
        },
      })
    );

    return () => {
      dispatch(baoCaoClearChiTietHoTroThanhVien());
    };
  }, [dispatch, nguoiDungId, appliedFrom, appliedTo]);

  useEffect(() => {
    if (!errorChiTietHoTroThanhVien) return;
    toast.error(getErrMsg(errorChiTietHoTroThanhVien) || 'Không thể tải chi tiết hỗ trợ.');
  }, [errorChiTietHoTroThanhVien]);

  const rows = useMemo(() => {
    return Array.isArray(chiTietHoTroThanhVien) ? chiTietHoTroThanhVien : [];
  }, [chiTietHoTroThanhVien]);

  useEffect(() => {
    setActivePage(1);
  }, [rows.length, pageSize, appliedFrom, appliedTo]);

  const totalItems = rows.length;
  const startIndex = (activePage - 1) * pageSize;

  const pagedRows = useMemo(() => {
    return rows.slice(startIndex, startIndex + pageSize);
  }, [rows, startIndex, pageSize]);

  const onChangeShowNumber = (e) => {
    const newSize = parseInt(e.target.value, 10) || 20;
    setPageSize(newSize);
    setActivePage(1);
  };

  const summary = useMemo(() => {
    let total = 0;
    let done = 0;
    let doing = 0;

    rows.forEach((r) => {
      const c = getKhoaCount(r);
      total += c;

      if (isHoTroDone(r)) done += c;
      else doing += c;
    });

    return { total, done, doing };
  }, [rows]);

  const tenThanhVien =
    (rows[0]?.nguoiHoTro ?? rows[0]?.NguoiHoTro ?? rows[0]?.tenThanhVien ?? rows[0]?.TenThanhVien) || '';

  if (!nguoiDungId) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning mb-0">Thiếu nguoiDungId trên URL.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-start mb-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => {
            if (returnUrl) {
              history.push(decodeURIComponent(returnUrl));
              return;
            }
            history.goBack();
          }}
          title="Quay lại"
        >
          ← Quay lại
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-column">
            <h3 className="card-title mb-0 float-none">Chi tiết Hỗ trợ phần mềm</h3>

            {tenThanhVien ? (
              <div className="text-muted mt-1">
                Nhân viên: <b>{tenThanhVien}</b>
              </div>
            ) : null}
          </div>
        </div>

        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <div style={{ minWidth: 220 }}>
              <label className="form-label mb-1">Khoảng thời gian</label>
              <select
                className="form-select form-select-sm"
                value={rangeLabel}
                onChange={handleChangeRange}
                disabled={loadingChiTietHoTroThanhVien}
              >
                <option value="">-- Chọn --</option>
                {RANGE_OPTIONS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {rangeLabel === RANGE_CUSTOM && (
              <>
                <div>
                  <label className="form-label mb-1">Từ ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={loadingChiTietHoTroThanhVien}
                  />
                </div>

                <div>
                  <label className="form-label mb-1">Đến ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={loadingChiTietHoTroThanhVien}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleApplyCustomRange}
                  disabled={loadingChiTietHoTroThanhVien}
                >
                  Lọc
                </button>
              </>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearFilter}
              disabled={loadingChiTietHoTroThanhVien}
            >
              Xóa lọc
            </button>

            {(appliedFrom || appliedTo) && (
              <div className="filter-hint" style={{ width: '100%' }}>
                Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between mb-3">
            <form className="form-inline">
              <label className="mr-2">Hiển thị :</label>
              <select className="form-control" value={pageSize} onChange={onChangeShowNumber}>
                {objectShowNumber.map((item, idx) => (
                  <option key={idx} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </select>
            </form>
            <div />
          </div>

          <div className="d-flex justify-content-center mb-3" style={{ gap: 24 }}>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Tổng: <span style={{ color: 'red' }}>{summary.total}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Đang thực hiện: <span style={{ color: 'red' }}>{summary.doing}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Hoàn thành: <span style={{ color: 'red' }}>{summary.done}</span>
            </div>
          </div>

          {loadingChiTietHoTroThanhVien ? (
            <div>Đang tải...</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  className="align-middle w-100"
                  style={{ minWidth: '1700px' }}
                >
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: '70px' }}>STT</th>
                      <th className="text-center" style={{ width: '150px' }}>Lĩnh vực</th>
                      <th className="text-center" style={{ width: '240px' }}>Khoa hỗ trợ</th>
                      <th className="text-center" style={{ width: '90px' }}>Số lượng</th>
                      <th className="text-center" style={{ width: '130px' }}>Ngày hỗ trợ</th>
                      <th className="text-center" style={{ width: '420px' }}>Nội dung</th>
                      <th className="text-center" style={{ width: '150px' }}>Loại lỗi</th>
                      <th className="text-center" style={{ width: '180px' }}>Mức độ phức tạp</th>
                      <th className="text-center" style={{ width: '140px' }}>Trạng thái</th>
                      <th className="text-center" style={{ width: '300px' }}>Ghi chú</th>
                    </tr>
                  </thead>

                  {pagedRows && pagedRows.length > 0 ? (
                    <tbody>
                      {pagedRows.map((x, idx) => {
                        const linhVuc =
                          x?.linhVuc ?? x?.LinhVuc ?? x?.tenLinhVuc ?? x?.TenLinhVuc ?? '';

                        const khoaHoTro =
                          x?.khoaHoTro ??
                          x?.KhoaHoTro ??
                          (Array.isArray(x?.khoaMas) ? x.khoaMas.join(', ') : '');

                        const soLuongKhoa = getKhoaCount(x);

                        const ngayHoTro =
                          x?.ngayHoTro ?? x?.NgayHoTro ?? x?.createdDate ?? x?.CreatedDate;

                        const noiDung = x?.noiDung ?? x?.NoiDung ?? '';
                        const ghiChu = x?.ghiChu ?? x?.GhiChu ?? '';

                        const loaiLoi = x?.loaiLoi ?? x?.LoaiLoi;
                        const mucDoPhucTap = x?.mucDoPhucTap ?? x?.MucDoPhucTap;
                        const status = x?.status ?? x?.Status ?? x?.statusValue ?? x?.StatusValue;

                        return (
                          <tr key={`${idx}-${x?.id ?? x?.Id ?? ''}`}>
                            <td className="text-center" style={{ width: '70px' }}>
                              {startIndex + idx + 1}
                            </td>

                            <td
                              style={{ ...textCellStyle, width: '150px' }}
                              title={linhVuc || ''}
                            >
                              {linhVuc || <span className="text-muted"></span>}
                            </td>

                            <td
                              style={{ ...textCellStyle, width: '240px' }}
                              title={khoaHoTro || ''}
                            >
                              {khoaHoTro || <span className="text-muted"></span>}
                            </td>

                            <td className="text-center" style={{ width: '90px' }}>
                              {soLuongKhoa}
                            </td>

                            <td className="text-center" style={{ width: '130px' }}>
                              {fmtDate(ngayHoTro) || <span className="text-muted"></span>}
                            </td>

                            <td
                              style={{ ...contentCellStyle, width: '420px' }}
                              title={noiDung || ''}
                            >
                              {noiDung || <span className="text-muted"></span>}
                            </td>

                            <td
                              className="text-center"
                              style={{ ...cellWrapStyle, width: '150px' }}
                              title={mapLoaiLoiLabel(loaiLoi) || ''}
                            >
                              {mapLoaiLoiLabel(loaiLoi) || <span className="text-muted"></span>}
                            </td>

                            <td
                              className="text-center"
                              style={{ ...cellWrapStyle, width: '180px' }}
                              title={mapMucDoPhucTapLabel(mucDoPhucTap) || ''}
                            >
                              {mapMucDoPhucTapLabel(mucDoPhucTap) || <span className="text-muted"></span>}
                            </td>

                            <td
                              className="text-center"
                              style={{ ...cellWrapStyle, width: '140px' }}
                              title={mapTrangThaiHoTroLabel(status) || ''}
                            >
                              {mapTrangThaiHoTroLabel(status) || <span className="text-muted"></span>}
                            </td>

                            <td
                              style={{ ...contentCellStyle, width: '300px' }}
                              title={ghiChu || ''}
                            >
                              {ghiChu || <span className="text-muted"></span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan="10" className="text-center">
                          Không có dữ liệu
                        </td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              </div>

              <div className="dataTables_paginate paging_simple_numbers mt-2">
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  innerClass="pagination pagination-sm no-margin pull-right"
                  activePage={activePage}
                  itemsCountPerPage={Number(pageSize)}
                  totalItemsCount={totalItems}
                  pageRangeDisplayed={5}
                  onChange={(p) => setActivePage(p)}
                  firstPageText="«"
                  prevPageText="‹"
                  nextPageText="›"
                  lastPageText="»"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaoCaoChiTietHoTroThanhVien;
